import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Copy,
  Database,
  FolderArchive,
  KeyRound,
  Loader2,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-react';
import { fetchExportApercu, preparerExport } from '../services/api';

/**
 * Exporter tout : la base en CSV, les documents en ZIP.
 *
 * Ce bouton est une promesse tenue en un clic : vos données vous appartiennent,
 * et vous partez avec quand vous voulez — sans écrire à personne, sans attendre
 * de réponse, sans résilier d'abord. Un export qu'il faut demander n'est pas un
 * droit, c'est une faveur.
 *
 * Deux archives, et c'est voulu : les chiffres (CSV, une table par fichier) se
 * rouvrent dans n'importe quel tableur ; les documents — certificats, factures
 * PDF, visuels — pèsent parfois des centaines de méga-octets et n'ont rien à
 * faire dans le même téléchargement que trente kilo-octets de chiffres.
 *
 * L'archive part désormais chiffrée
 * ---------------------------------------------------------------------------
 * Elle contient le registre entier des adhérents, avec leurs adresses et leurs
 * coordonnées. Elle circulera par courriel ou sur une clé USB — c'est à cela
 * qu'elle sert — et elle partait jusqu'ici en clair : qui l'interceptait, la
 * lisait.
 *
 * Le mot de passe est engendré pour cet export-là et montré une seule fois. Il
 * n'est pas votre mot de passe de connexion, et ce n'est pas une commodité :
 * celui-ci n'est pas lisible, il n'existe qu'en empreinte, et c'est ce qui le
 * protège.
 *
 * Le téléchargement reste un lien que le navigateur suit lui-même : une archive
 * lourde n'a pas à passer par du JavaScript. Le mot de passe se demande donc
 * juste avant, par un appel ordinaire, et s'affiche pendant que le
 * téléchargement se fait.
 */
export default function ExporterMesDonnees() {
  const apercu = useQuery({ queryKey: ['export-apercu'], queryFn: fetchExportApercu });

  const [motDePasse, setMotDePasse] = useState<string | null>(null);
  const [bits, setBits] = useState(0);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [copie, setCopie] = useState(false);

  const fichiers = apercu.data?.fichiers ?? [];
  const lignes = apercu.data?.lignes ?? 0;

  const telecharger = async () => {
    setErreur(null);
    setEnCours(true);
    try {
      const prepare = await preparerExport();
      setMotDePasse(prepare.motDePasse);
      setBits(prepare.bits);
      setCopie(false);

      // Le lien part une fois le mot de passe affiché : si le téléchargement
      // commençait d'abord, le navigateur pourrait masquer la page et le mot de
      // passe ne serait jamais lu.
      window.location.href = `/api/admin/export/archive?jeton=${encodeURIComponent(prepare.jeton)}`;
    } catch {
      setErreur("La préparation a échoué. L'export n'a pas été lancé — réessayez.");
    } finally {
      setEnCours(false);
    }
  };

  const copier = async () => {
    if (!motDePasse) return;
    try {
      await navigator.clipboard.writeText(motDePasse);
      setCopie(true);
    } catch {
      setCopie(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Tout ce que votre espace contient, dans des formats qui se rouvrent partout : les données en
        CSV — une table par fichier, lisible dans n'importe quel tableur — et les documents déposés
        ou générés dans un ZIP qui garde leur classement. Vos données vous appartiennent : cet
        export ne se demande pas, il se clique.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={telecharger}
          disabled={enCours}
          className="text-left rounded-xl border border-gray-200 dark:border-gray-700 p-4 hover:border-primary-400 dark:hover:border-primary-600 transition-colors block disabled:opacity-60"
        >
          <p className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
            {enCours ? (
              <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
            ) : (
              <Database className="w-4 h-4 text-primary-600" />
            )}
            Les données (CSV, protégées)
          </p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {apercu.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin inline" />
            ) : (
              <>
                {fichiers.length} fichier{fichiers.length > 1 ? 's' : ''},{' '}
                {lignes.toLocaleString('fr-FR')} ligne{lignes > 1 ? 's' : ''} au total. Membres,
                cotisations, factures, dons, réservations — sans les mots de passe ni les jetons.
              </>
            )}
          </p>
        </button>

        <a
          href="/api/admin/export/documents"
          className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 hover:border-primary-400 dark:hover:border-primary-600 transition-colors block"
        >
          <p className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
            <FolderArchive className="w-4 h-4 text-primary-600" />
            Les documents (ZIP)
          </p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Tout ce qui a été déposé ou généré : pièces des adhérents, factures et devis PDF,
            visuels, étiquettes. L'archive peut être lourde — laissez le téléchargement aller à son
            terme.
          </p>
        </a>
      </div>

      {erreur && (
        <p className="flex items-start gap-2 rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-800 dark:text-red-200">
          <TriangleAlert className="w-4 h-4 shrink-0 mt-0.5" />
          {erreur}
        </p>
      )}

      {motDePasse && (
        <div className="rounded-xl border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-4 space-y-3">
          <p className="flex items-center gap-2 font-semibold text-amber-900 dark:text-amber-100">
            <KeyRound className="w-4 h-4 shrink-0" />
            Le mot de passe de cette archive — notez-le maintenant
          </p>

          <div className="flex items-center gap-2 flex-wrap">
            <code className="flex-1 min-w-0 select-all rounded-lg bg-white dark:bg-gray-900 border border-amber-300 dark:border-amber-700 px-3 py-2 font-mono text-base sm:text-lg tracking-wider text-gray-900 dark:text-gray-100 break-all">
              {motDePasse}
            </code>
            <button
              type="button"
              onClick={copier}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-medium text-amber-900 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-900/40"
            >
              <Copy className="w-4 h-4" />
              {copie ? 'Copié' : 'Copier'}
            </button>
          </div>

          <p className="text-sm text-amber-900/90 dark:text-amber-100/90">
            Il ne sera plus affiché. Sans lui, l'archive reste illisible — c'est ce qui la protège si
            elle s'égare. L'éditeur peut le retrouver, mais uniquement sur demande justifiée, et
            cette demande s'inscrit au journal de votre association.
          </p>

          <p className="flex items-start gap-2 text-sm text-amber-900/90 dark:text-amber-100/90">
            <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              Chiffrement AES-256, {bits} bits d'entropie. L'explorateur de Windows et l'utilitaire
              d'archive de macOS ne savent pas ouvrir ce format : utilisez{' '}
              <strong>7-Zip</strong> (Windows), <strong>Keka</strong> (macOS) ou{' '}
              <strong>WinRAR</strong>. Le format historique du ZIP, lui, s'ouvre partout mais se
              casse en quelques secondes sur un fichier dont on devine le début — et un CSV commence
              toujours par des en-têtes connus.
            </span>
          </p>
        </div>
      )}

      {fichiers.length > 0 && (
        <details className="text-xs text-gray-500 dark:text-gray-400">
          <summary className="cursor-pointer">Le détail des fichiers CSV</summary>
          <ul className="mt-1.5 columns-2 sm:columns-3 gap-4">
            {fichiers.map((f) => (
              <li key={f.fichier} className="flex justify-between gap-2 break-inside-avoid">
                <span className="font-mono">{f.fichier}</span>
                <span className="tabular-nums shrink-0">{f.lignes}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
